<!--
 * @Author: YuanYuan
 * @Date: 2024-02-06 10:45:22
 * @LastEditTime: 2025-09-08 16:04:43
 * @description: 业务带宽
-->
<template>
  <div>
    <el-row :gutter="16">
      <el-col v-bind="$const['Common/SearchCol']">
        <QuerySelect
          v-model="searchForm.businessType"
          :data="$const['Usage/BusinessType']"
          :label="$t('usage.businessType')"
          :all-label="$t('common.all')"
          :all-value="1" />
      </el-col>
      <template v-if="searchForm.businessType===2">
        <el-col v-bind="$const['Common/SearchCol']">
          <QuerySelect
            v-model="searchForm.groupId"
            v-loading="groupLoading"
            :data="groupOptions"
            :label="$t('common.domainGroup')"
            :all-label="$t('common.allDomainGroups')"
            :all-value="-1"
            label-key="groupName"
            value-key="groupId"
            @change="queryDomainOptions" />
        </el-col>
        <el-col v-bind="$const['Common/SearchCol']">
          <QuerySelectSearch
            v-model="searchForm.domains"
            v-loading="domainLoading"
            :data="domainOptions"
            label-key="domain"
            value-key="domain"
            :label="$t('common.domain')" />
        </el-col>
      </template>
      <el-col v-bind="$const['Common/SearchCol']">
        <el-button @click="queryHandler" type="primary">{{$t('common.query')}}</el-button>
      </el-col>
    </el-row>
    <div class="flex-row-between-center mt-16 mb-24">
      <div v-for="(item, index) in summaryData" :key="index" class="summary-block">
        <span class="summary-block_name">{{item.text}}</span>
        <div>
          <span class="summary-block_value">{{item.value}}</span>
          <span v-if="index === 0" class="summary-block_time">[{{peakTime}}]</span>
        </div>
      </div>
    </div>
    <LineChart :options="lineChartData" v-loading="loading" @legend-click="legendClick" />
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, reactive, onMounted, toRefs, watch } from 'vue'
import LineChart from '@/components/echarts/Line.vue'
import { useDomainGroups } from '@/utils/hooks'
import { beijingTime2TerminalTime } from '@/utils/time'
import { assign } from 'lodash-es'
import { constGet } from '@/utils/base'

const $vm = getCurrentInstance().proxy

const legendNameMap = {
  normal: $vm.$t('usage.bandwidth') + $vm.$t('common.bracket', { name: 'Mbps' }), // 业务带宽
  special: $vm.$t('usage.highPerfBandwidth') + $vm.$t('common.bracket', { name: 'Mbps' }), // 高性能链路带宽
  total: $vm.$t('usage.totalBandwidth') + $vm.$t('common.bracket', { name: 'Mbps' }) // 总带宽
}

const props = defineProps({
  topParams: {
    type: Object,
    default: () => ({
      packageId: '',
      beijingStartTime: '',
      beijingEndTime: ''
    })
  }
})
const { topParams } = toRefs(props)

const bandwidthData = ref({})
const searchForm = reactive({
  businessType: 2,
  groupId: -1,
  domains: []
})
const groupLoading = ref(false)
const groupOptions = ref([])
const domainOptions = ref([])
const domainLoading = ref(false)
const summaryData = ref([
  { text: $vm.$t('usage.peakBandwidth'), value: '0.00Mbps' },
  { text: $vm.$t('usage.totalTraffic'), value: '0.00GB' },
  { text: '', value: '0.00Mbps' }
])
const peakTime = ref('-')
const loading = ref(false)
let lineChartData = reactive({
  xAxis: {
    data: []
  },
  series: []
})

const legendClick = params => {
  const { selected } = params
  if (selected[legendNameMap.normal] && !selected[legendNameMap.special] && !selected[legendNameMap.total]) {
    setSummaryValue('normal')
    summaryData.value[2].text = constGet('Usage/BillingMethodOptions', 2)
  } else if (selected[legendNameMap.special] && !selected[legendNameMap.normal] && !selected[legendNameMap.total]) {
    setSummaryValue('special')
    const textType = bandwidthData.value.special.costType
    summaryData.value[2].text = constGet('Usage/BillingMethodOptions', textType)
  } else if (!selected[legendNameMap.total] && !selected[legendNameMap.normal] && !selected[legendNameMap.special]) {
    setSummaryValue()
    summaryData.value[2].text = constGet('Usage/BillingMethodOptions', 0)
  } else {
    setSummaryValue('total')
    summaryData.value[2].text = constGet('Usage/BillingMethodOptions', 0)
  }
}

const setSummaryValue = type => {
  if (type && bandwidthData.value[type]) {
    const typeData = bandwidthData.value[type]
    summaryData.value[0].value = typeData?.peakValue?.indexOf('Mbps') > -1 ? typeData?.peakValue : `${typeData?.peakValue||0}Mbps`
    summaryData.value[1].value = typeData?.totalFlow?.indexOf('GB') > -1 ? typeData?.totalFlow : `${typeData?.totalFlow||0}GB`
    summaryData.value[2].value = `${typeData?.costValue||0}Mbps`
    peakTime.value = beijingTime2TerminalTime(typeData?.peakTime)
  } else {
    summaryData.value[0].value = '0.00Mbps'
    summaryData.value[1].value = '0.00GB'
    summaryData.value[2].value = '0.00Mbps'
    peakTime.value = ''
  }
}

const splitDomains = () => {
  const portList = []
  const domainList = []
  searchForm.domains.forEach(n => {
    if (/^\d+\(\w+\)$/.test(n)) {
      portList.push(n)
    } else {
      domainList.push(n) 
    }
  })
  return {
    ports: portList.join(','),
    domains: domainList.join(',')
  }
}

const getParams = () => {
  const { domains, ports } = splitDomains()
  const params = {
    package_id: topParams.value.packageId,
    begin_time: topParams.value.beijingStartTime,
    end_time: topParams.value.beijingEndTime,
    groupId: searchForm.groupId,
    businessType: searchForm.businessType
  }
  if (searchForm.businessType === 1) {
    params.domain_list = domains
  } else if (searchForm.businessType === 2) {
    params.domain_list = domains
    params.port_list = ports
  }
  return params
}

const queryHandler = () => {
  loading.value = true
  $vm.$api['usage/bandwidthQuery'](getParams()).then(res => {
    if(res?.code === 200 && res?.data) {
      bandwidthData.value = res.data
      setSummaryValue('total')
      if (res.data?.total?.detail?.length) {
        const xAxisData = res.data.total.detail.map(n => beijingTime2TerminalTime(n.time))
        lineChartData = assign(lineChartData, {
          xAxis: {
            data: xAxisData
          },
          series: [
            { data: res.data.total.detail.map(n => n.value), name: legendNameMap.total, color: '#47cbff' }
          ]
        })
        if (res.data?.normal?.detail?.length) {
          lineChartData.series.unshift({
            data: res.data.normal.detail.map(n => n.value), name: legendNameMap.normal,
            color: '#45de7f'
          })
        }
        if (res.data?.special?.detail?.length) {
          lineChartData.series.unshift({
            data: res.data.special.detail.map(n => n.value), name: legendNameMap.special,
            color: '#ff7a33'
          })
        }
      }
    }
  }).finally(() => (loading.value = false))
}

const queryDomainOptions = () => {
  if (topParams.value.packageId) {
    domainLoading.value = true
    $vm.$api['usage/bandwidthDomains']({
      packageId: topParams.value.packageId,
      startTime: topParams.value.beijingStartTime,
      endTime: topParams.value.beijingEndTime,
      groupId: searchForm.groupId
    }).then(res => {
      if (res?.code === 200 && res?.data?.length) {
        domainOptions.value = res.data.map(n => ({ domain: n }))
        if (searchForm.domains.length) {
          // 保留之前选择的且仍在新数据中存在的域名
          searchForm.domains = searchForm.domains.filter(n => res.data.includes(n))
        } else {
          searchForm.domains = res.data
        }
        queryHandler()
      } else {
        domainOptions.value = []
        searchForm.domains = []
        queryHandler()
      }
    }).finally(() => (domainLoading.value = false))
  }
}

watch(() => topParams.value, () => {
  queryDomainOptions()
}, { deep: true, immediate: true })

const getDomainGroupOptions = () => {
  groupLoading.value = true
  useDomainGroups().then(data => {
    groupOptions.value = data
  }).finally(() => (groupLoading.value = false))
}

onMounted(() => {
  getDomainGroupOptions()
  summaryData.value[2].text = constGet('Usage/BillingMethodOptions', 0)
})
</script>

<style lang="scss" scoped>
@import './summaryStyle.scss';
</style>
